import Box from "@/common/components/Box";
import ButtonLink from "@/common/components/ButtonLink";
import ImageWithDisplayName from "@/common/components/ImageWithDisplayName";
import ResumeBox from "@/common/components/ResumeBox";
import { EstablishmentCompanyData } from "@/common/types/establishment/company.type";
import { formatCurrency, formatDateTime } from "@/common/utils/formatters";
import { negotiationProductValueLabelByAction } from "@/features/negotiations/constants/negotiationStatus.constant";
import { NegotiationProductHistoryData } from "@/features/negotiations/types/negotiations.type";
import { CompanyData } from "@/features/privateCompanies/types/companies.type";
import {
  BriefcaseBusinessIcon,
  Building2Icon,
  CalendarDaysIcon,
} from "lucide-react";
import { twMerge } from "tailwind-merge";

interface NegotiatingProductHistoryItemProps {
  negotiationproductHistory: NegotiationProductHistoryData;
  clientCompany?: CompanyData;
  establishment?: EstablishmentCompanyData;
}

const NegotiatingProductHistoryItem = ({
  negotiationproductHistory,
  clientCompany,
  establishment,
}: NegotiatingProductHistoryItemProps) => {
  return (
    <div key={negotiationproductHistory.id} className="flex flex-col gap-3">
      <div className="flex justify-between items-center">
        <div className="flex flex-col gap-1">
          {negotiationproductHistory.author_type === "Client::User" ? (
            <ButtonLink
              linkTo={`/companies/${clientCompany?.id}`}
              operationKlassToCheckPermission="Admin::Client::Companies::Show"
              hoverBackgroundSize="small"
              className="w-[280px]"
            >
              <ImageWithDisplayName
                placeholderIcon={BriefcaseBusinessIcon}
                imageUrl={clientCompany?.general_information?.company_logo?.url}
                displayName={clientCompany?.general_information?.fantasy_name}
                displayNameClassName="line-clamp-1 text-start"
                containerClassName="w-full"
              />
            </ButtonLink>
          ) : negotiationproductHistory.author_type ? (
            <ButtonLink
              linkTo={`/establishments/${establishment?.id}`}
              operationKlassToCheckPermission="Admin::Establishment::Companies::Show"
              hoverBackgroundSize="small"
              className="max-w-[280px]"
            >
              <ImageWithDisplayName
                placeholderIcon={Building2Icon}
                imageUrl={establishment?.general_information?.company_logo?.url}
                displayName={establishment?.general_information?.fantasy_name}
                displayNameClassName="line-clamp-1 text-start"
              />
            </ButtonLink>
          ) : (
            <></>
          )}

          <p className="italic text-sm font-light text-white-300 mt-0.5">
            por {negotiationproductHistory.author.full_name}
          </p>
        </div>

        {negotiationproductHistory.created_at && (
          <div className="flex items-center gap-1">
            <CalendarDaysIcon strokeWidth={1.5} size={16} />

            <p className="font-light">
              {formatDateTime(new Date(negotiationproductHistory.created_at))}
            </p>
          </div>
        )}
      </div>

      <Box
        className={twMerge(
          "p-4 rounded-lg flex flex-col gap-4 bg-white-200",
          negotiationproductHistory.action === "accept" && "bg-success-op",
          negotiationproductHistory.action === "reject" && "bg-danger-op",
        )}
      >
        <ResumeBox
          label={
            negotiationProductValueLabelByAction[
              (negotiationproductHistory.action ||
                "counter") as keyof typeof negotiationProductValueLabelByAction
            ]
          }
          value={formatCurrency(Number(negotiationproductHistory.value))}
        />

        {negotiationproductHistory.observation && (
          <ResumeBox
            label="Justificativa"
            value={negotiationproductHistory.observation}
          />
        )}
      </Box>
    </div>
  );
};

export default NegotiatingProductHistoryItem;
